import {
  Dialog, DialogContent, Box, Typography, Button, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { COLORS, FONT_HEADING, BUTTON_HOVER_SX } from '../../talkTreeShared/Tree/constants/theme';

const BLUE = COLORS.primary;

// Diagram lives in the app's public folder.
const DIAGRAM_SRC = '/TTDataProcessing.png';
const PRIVACY_URL = 'https://www.talktrees.ai/privacy';

const INTRO = 'At Talk Trees, you are always in control of what classroom discussions you choose to upload or stream for analysis. Our process is designed to protect student and teacher privacy while creating meaningful insights about classroom talk.';

const STEPS = [
  {
    title: 'You upload or stream a classroom discussion',
    body: 'You choose which classroom discussion to analyze by either uploading an audio recording (or transcript) or using a supported streaming option. For privacy purposes, Talk Trees only accepts audio or transcripts, not video.',
  },
  {
    title: 'Data is secured',
    body: 'All data is encrypted both in transit and at rest using industry-standard security practices.',
  },
  {
    title: 'A transcript gets generated',
    body: 'Talk Trees uses automated speech recognition (ASR) to convert the audio into a text transcript.',
  },
  {
    title: 'The audio is deleted',
    body: 'Once transcription is complete, Talk Trees immediately deletes the audio file and does not store it long-term.',
  },
  {
    title: 'The transcript is anonymized',
    body: 'Talk Trees runs the transcript through an anonymization component that removes all names and replaces the original transcript.',
  },
  {
    title: 'We identify speakers',
    body: 'Talk Trees uses a diarization component to distinguish which parts of the transcript are teacher talk and which are student talk.',
  },
  {
    title: 'We analyze student talk',
    body: 'Talk Trees applies multiple AI models to analyze the types of talk and the academic quality of classroom discourse.',
  },
  {
    title: 'We generate your Talk Tree',
    body: 'An encrypted version of the fully anonymized transcript is used to generate your Talk Tree and related classroom talk insights.',
  },
  {
    title: 'Transcript is deleted',
    body: 'The transcript is immediately deleted after tree is generated. Only the data used to generate the tree is kept.',
  },
];

const BODY_SX = { fontSize: '15px', color: COLORS.text, lineHeight: 1.6 };

const StepNumber = ({ n }) => (
  <Box
    sx={{
      width: 46,
      height: 46,
      borderRadius: '50%',
      backgroundColor: COLORS.primaryLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>
      {n}
    </Typography>
  </Box>
);

export default function ManageHelpDialog({ open, onClose }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="manage-help-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          backgroundColor: COLORS.page,
          p: { xs: 2, sm: 3 },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{ position: 'absolute', right: 16, top: 16, color: '#111827', zIndex: 1 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ pt: 1 }}>
        <Typography
          id="manage-help-dialog-title"
          component="h2"
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: { xs: '26px', sm: '32px' },
            fontWeight: 400,
            color: BLUE,
            mb: 4,
            pr: 5,
          }}
        >
          How we process your data
        </Typography>

        <Typography sx={{ ...BODY_SX, fontSize: '17px', mb: 3 }}>
          {INTRO}
        </Typography>

        <Box
          component="img"
          src={DIAGRAM_SRC}
          alt="Diagram of how Talk Trees processes your data: a classroom discussion is streamed or uploaded and encrypted, the audio is transcribed and then deleted, the transcript is anonymized, teacher and student talk are distinguished, student talk is analyzed for quality of discourse, a Talk Tree is generated, and the transcript is deleted."
          sx={{ display: 'block', width: '100%', height: 'auto', mb: 4 }}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
          {STEPS.map((step, i) => (
            <Box key={step.title} sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
              <StepNumber n={i + 1} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '17px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                  {step.title}
                </Typography>
                <Typography sx={BODY_SX}>{step.body}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography sx={{ ...BODY_SX, fontSize: '17px', mt: 4 }}>
          Talk Trees is designed with student privacy in mind. Your Talk Trees are visible only to you and those you choose to share them with. Learn more in our Privacy Policy:{' '}
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            {PRIVACY_URL}
          </a>
          .
        </Typography>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            mt: 4,
            py: 1.4,
            px: 3,
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '4px',
            backgroundColor: COLORS.primaryDark,
            boxShadow: 'none',
            '&:hover': BUTTON_HOVER_SX,
          }}
        >
          I'm done
        </Button>
      </DialogContent>
    </Dialog>
  );
}